import { keyframes } from '@emotion/react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

const GRADIENT = 'linear-gradient(135deg, #8a2be2, #FFD700)';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PAIN_POINTS = [
  {
    lordicon: '/img/angry.json',
    title: '完走証やタイムの管理が面倒',
    description: 'タイムを一元管理できないし、クラウドストレージに保存しても一覧性が悪く探すのが大変',
  },
  {
    lordicon: '/img/head.json',
    title: '記録の分析がしづらい',
    description:
      '同じ大会やシーズンの記録を比較したいのに、完走証のファイルを探すのが手間。都度ファイルを開くのも面倒。',
  },
  {
    lordicon: '/img/question.json',
    title: 'デジタルだと所有感がない',
    description: 'デジタルは形がないだけに紙の完走証のような所有感も"自分だけのもの"感もない',
  },
];

const FEATURES = [
  {
    lordicon: '/img/upload.json',
    title: '簡単な記録管理',
    description: '完走証をアップロードするだけで、自動的に記録を解析して保存',
  },
  {
    lordicon: '/img/compare.json',
    title: '大会別の記録比較',
    description: '高い一覧性とフィルタリング機能で、同じ大会の記録を簡単に比較可能',
  },
  {
    lordicon: '/img/blockchain.json',
    title: '記録に"所有感"',
    description:
      'ブロックチェーンに記録を永続的に保存することでデータの所有権・唯一性を付与し、記録の所有感や"自分だけのもの"感を演出',
  },
];

const STEPS = [
  {
    title: 'ファイルをアップロード',
    description: '大会から発行された完走証のPDFファイルをアップロードしてください。',
    note: '※ 現在はPDFファイルのみ対応。今後、JPG、PNG等の画像ファイルにも対応予定です。',
    image: '/img/step1.png',
    alt: 'ファイルアップロード画面',
  },
  {
    title: '解析結果を確認＆発行',
    description:
      '完走証から開催日、大会名、グロスタイム（スタート号砲からのタイム）、ネットタイム（計測チップ通過からのタイム）を自動で読み取ります。読み取った内容を確認し、「記録を保存する」ボタンをクリックするとデータがブロックチェーンに保存されます。',
    note: '※大会名が検出できない場合は登録できません。',
    image: '/img/step2.png',
    alt: '確認・発行画面',
  },
  {
    title: '記録の確認',
    description:
      '登録された記録はダッシュボード画面で一覧表示されます。フィルタリング機能を使うことで同大会の過去の記録との比較なども可能です。',
    image: '/img/step3.png',
    alt: '記録一覧画面',
  },
];

const GOALS = [
  {
    title: 'オープンなデータ連携',
    description: 'ブロックチェーンの特性を活かし、サービス間の積極的な連携を推進',
  },
  {
    title: 'web3の価値提供',
    description: 'ブロックチェーンならではの新しい体験と価値を創出',
  },
  {
    title: 'web3業界全体の発展',
    description: '業界全体の成長に貢献し、エコシステムを拡大',
  },
  {
    title: 'ブロックチェーンの社会実装',
    description: '実用的な活用事例を創出し、社会実装を加速',
  },
];

const METADATA_JSON = `{
  "event": "<EVENT_NAME>",
  "date": "<EVENT_DATE>",
  "year": "<YEAR>",
  "time": "<GROSS_TIME>",
  "net_time": "<NET_TIME>",
  "certificate_id": "<CERTIFICATE_ID>",
  "uid": "<USER_ID>",
  "is_valid": "<VALIDITY>"
}`;

function LordIconCard({ src }) {
  return (
    <Box sx={{ my: 3, mx: 'auto', display: 'flex', justifyContent: 'center' }}>
      <lord-icon src={src} trigger="hover" style={{ width: '10rem', height: '10rem' }} />
    </Box>
  );
}

function StepNumber({ number }) {
  return (
    <Box
      sx={{
        minWidth: 32,
        height: 32,
        borderRadius: '50%',
        background: GRADIENT,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: '0.875rem',
        mt: 0.5,
      }}
    >
      {number}
    </Box>
  );
}

export function LPPage() {
  const theme = useTheme();
  const [dialogImage, setDialogImage] = useState(null);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* ── Header ── */}
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 64,
          background: GRADIENT,
          display: 'flex',
          alignItems: 'center',
          px: { xs: 2, md: 4 },
          zIndex: theme.zIndex.appBar,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}
      >
        <Box component={RouterLink} to="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <Box component="img" src="/logo.png" alt="RunPort コレクト" sx={{ width: 48, mr: 1.5 }} />
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: '1.5rem',
              color: '#fff',
            }}
          >
            RunPort コレクト
          </Typography>
        </Box>
      </Box>

      {/* ── Main ── */}
      <Box component="main" sx={{ pt: '64px', flex: 1 }}>
        {/* Hero */}
        <Box sx={{ bgcolor: '#f8f9fa', py: { xs: 5, md: 8 } }}>
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid
                size={{ xs: 12, md: 6 }}
                sx={{
                  order: { xs: 2, md: 1 },
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                <Typography
                  variant="h3"
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    mb: 3,
                    animation: `${fadeInUp} 0.7s ease-out`,
                  }}
                >
                  記録に価値を
                </Typography>
                <Typography variant="h6" component="p" sx={{ mb: 1.5, fontWeight: 400, lineHeight: 1.8 }}>
                  RunPort
                  コレクトは、マラソン大会の完走証をアップロードするだけで自動で整理。過去の記録を簡単に振り返られます。
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 3,
                    justifyContent: { xs: 'center', md: 'flex-start' },
                  }}
                >
                  <CheckCircleIcon sx={{ color: 'primary.main', mr: 0.5, fontSize: 18 }} />
                  <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 700 }}>
                    無料でご利用いただけます
                  </Typography>
                </Box>
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="contained"
                  size="large"
                  sx={{ background: GRADIENT, px: 4, py: 1.5, fontSize: '1rem' }}
                >
                  Apple ID / Googleアカウントで始める
                </Button>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} sx={{ order: { xs: 1, md: 2 } }}>
                <Box
                  component="img"
                  src="/img/running.jpg"
                  alt="ゴールでの熱狂、そして達成感"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  }}
                />
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Pain Points */}
        <Container maxWidth="lg" sx={{ my: { xs: 5, md: 8 } }}>
          <Typography variant="h5" component="h2" textAlign="center" sx={{ mb: 4, fontWeight: 700 }}>
            RunPort コレクトはこんなことで困ってる方のためのサービスです
          </Typography>
          <Grid container spacing={3}>
            {PAIN_POINTS.map((item) => (
              <Grid key={item.title} size={{ xs: 12, md: 4 }}>
                <Card sx={{ height: '100%', textAlign: 'center' }}>
                  <CardContent sx={{ p: 4 }}>
                    <LordIconCard src={item.lordicon} />
                    <Typography variant="h6" component="h3" sx={{ mb: 2, fontSize: '1.1rem' }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* Features */}
        <Container maxWidth="lg" sx={{ my: { xs: 5, md: 8 } }}>
          <Typography variant="h5" component="h2" textAlign="center" sx={{ mb: 4, fontWeight: 700 }}>
            RunPort コレクトの特徴
          </Typography>
          <Grid container spacing={3}>
            {FEATURES.map((item) => (
              <Grid key={item.title} size={{ xs: 12, md: 4 }}>
                <Card sx={{ height: '100%', textAlign: 'center' }}>
                  <CardContent sx={{ p: 4 }}>
                    <LordIconCard src={item.lordicon} />
                    <Typography variant="h6" component="h3" sx={{ mb: 2, fontSize: '1.1rem' }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* How To Use */}
        <Container maxWidth="lg" sx={{ my: { xs: 5, md: 8 } }}>
          <Typography variant="h5" component="h2" textAlign="center" sx={{ mb: 4, fontWeight: 700 }}>
            使い方
          </Typography>
          <Card>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              {STEPS.map((step, index) => (
                <Box key={step.title} sx={{ mb: index < STEPS.length - 1 ? 5 : 0 }}>
                  <Grid container spacing={4} alignItems="center">
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <StepNumber number={index + 1} />
                        <Box>
                          <Typography variant="h6" component="h3" sx={{ mb: 1, fontSize: '1.1rem' }}>
                            {step.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                            {step.description}
                          </Typography>
                          {step.note && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                              {step.note}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ textAlign: 'center' }}>
                      <Box
                        component="img"
                        src={step.image}
                        alt={step.alt}
                        onClick={() => setDialogImage(step)}
                        sx={{
                          maxHeight: 450,
                          width: 200,
                          objectFit: 'contain',
                          borderRadius: 2,
                          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                          bgcolor: '#f8f9fa',
                          cursor: 'pointer',
                          transition: 'opacity 0.2s',
                          '&:hover': { opacity: 0.8 },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Container>

        {/* Technical Details */}
        <Container maxWidth="lg" sx={{ my: { xs: 5, md: 8 } }}>
          <Typography variant="h5" component="h2" textAlign="center" sx={{ mb: 4, fontWeight: 700 }}>
            技術仕様
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" component="h3" sx={{ mb: 3 }}>
                    ブロックチェーン仕様
                  </Typography>

                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    利用チェーン
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Soneium
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Soneium（ソニューム）は、ソニーグループのSony Block Solutions Labs Pte.
                    Ltd,が開発しているブロックチェーンです。
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <Link href="https://soneium.org" target="_blank" rel="noopener">
                      公式ドキュメント
                    </Link>
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3 }}>
                    <Link
                      href="https://www.sony.com/ja/SonyInfo/News/Press/202408/24-029/"
                      target="_blank"
                      rel="noopener"
                    >
                      ソニーグループのプレスリリース
                    </Link>
                  </Typography>

                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    コントラクトアドレス
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3 }}>
                    <Link
                      href="https://soneium.blockscout.com/address/0x9a5256037b20346D6Ee6714ae3ff67F50c439bb9"
                      target="_blank"
                      rel="noopener"
                    >
                      0x9a5256037b20346D6Ee6714ae3ff67F50c439bb9
                    </Link>
                  </Typography>

                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    保存データ
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    ブロックチェーンには最小限の記録データのみを保存します。大会名、開催日時、グロスタイム、ネットタイムが含まれます。個人情報並びに個人情報に繋がるデータ（名前、ゼッケン番号等）は一切保存されません。
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    なお、アップロードされた完走証はオフチェーンで管理するので、ご自身の手で保存先のURLを公開しない限り、外部から参照されることはありません。
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" component="h3" sx={{ mb: 3 }}>
                    データ連携仕様
                  </Typography>

                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    オープン性
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    ブロックチェーン上のデータは完全にオープンです。弊社への事前連絡なしで自由に連携いただけます。
                  </Typography>

                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    データ形式
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    扱いやすいように標準化されたJSON形式でMetadataとして保存されています。
                  </Typography>

                  <Box
                    component="pre"
                    sx={{
                      bgcolor: '#f8f9fa',
                      p: 2.5,
                      borderRadius: 2,
                      overflow: 'auto',
                      fontSize: '0.8rem',
                      fontFamily: '"Roboto Mono", monospace',
                      lineHeight: 1.7,
                      m: 0,
                    }}
                  >
                    {METADATA_JSON}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>

        {/* Web3 Appeal */}
        <Container maxWidth="lg" sx={{ my: { xs: 5, md: 8 } }}>
          <Typography variant="h5" component="h2" textAlign="center" sx={{ mb: 4, fontWeight: 700 }}>
            ブロックチェーンの可能性を、ともに。
          </Typography>
          <Card>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Grid container spacing={4}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h6" component="h3" sx={{ mb: 3 }}>
                    オープンな連携を目指して
                  </Typography>
                  {[
                    'ブロックチェーンを活用しながら中央集権的で閉じたサービスを展開していては、Why web3?に対する本質的な回答はできないと考えています。',
                    '中央集権的なコミュニティ構築はブロックチェーンを使わずともできます。実際、世の中には素晴らしいコミュニティを持つサービスが数多く存在します。',
                    '数多く投げかけられるWhy web3?の問いに答えるべく、ブロックチェーンの特性を活かした積極的なデータ連携で、新しい価値を共に創造していきましょう。',
                    'そして、ブロックチェーンの活用事例を増やし社会実装を共に推進していきましょう。',
                  ].map((text) => (
                    <Typography
                      key={text.slice(0, 20)}
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2, lineHeight: 1.8 }}
                    >
                      {text}
                    </Typography>
                  ))}
                  <Typography variant="body2" sx={{ textDecoration: 'underline', lineHeight: 1.8 }}>
                    web3サービスの運営者の皆様、ご連絡をお待ちしております。
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      bgcolor: '#f8f9fa',
                      p: 4,
                      borderRadius: 4,
                      height: '100%',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 3 }}>
                      私たちが目指すこと
                    </Typography>
                    {GOALS.map((goal) => (
                      <Box
                        key={goal.title}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          mb: 2.5,
                        }}
                      >
                        <CheckCircleIcon
                          sx={{
                            color: 'primary.main',
                            mr: 1.5,
                            mt: 0.25,
                            fontSize: 20,
                          }}
                        />
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            {goal.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {goal.description}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Container>

        {/* CTA */}
        <Box
          sx={{
            bgcolor: '#f8f9fa',
            py: { xs: 5, md: 7 },
            textAlign: 'center',
            mt: 4,
          }}
        >
          <Container maxWidth="sm">
            <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 700 }}>
              さあ、記録を残そう
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              完走証の新しい価値を、一緒に見つけましょう
            </Typography>
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              size="large"
              sx={{ background: GRADIENT, px: 5, py: 1.5 }}
            >
              無料で始める
            </Button>
          </Container>
        </Box>
      </Box>

      {/* ── Footer ── */}
      <Box
        component="footer"
        sx={{
          background: GRADIENT,
          color: '#fff',
          textAlign: 'center',
          py: 3,
          mt: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: { xs: 2, md: 4 },
            flexWrap: 'wrap',
            mb: 2,
          }}
        >
          <Link href="https://swandive.co.jp" target="_blank" rel="noopener" sx={{ color: '#fff' }}>
            運営会社
          </Link>
          <Link href="https://2dot99.com/tos.html" target="_blank" rel="noopener" sx={{ color: '#fff' }}>
            利用規約
          </Link>
          <Link href="https://2dot99.com/privacypolicy.html" target="_blank" rel="noopener" sx={{ color: '#fff' }}>
            プライバシーポリシー
          </Link>
        </Box>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          &copy;2026 Swandive, Inc.
        </Typography>
      </Box>

      {/* ── Image Dialog ── */}
      <Dialog open={!!dialogImage} onClose={() => setDialogImage(null)} maxWidth="sm">
        <IconButton
          onClick={() => setDialogImage(null)}
          aria-label="閉じる"
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>
        {dialogImage && (
          <Box sx={{ p: 2 }}>
            <Box
              component="img"
              src={dialogImage.image}
              alt={dialogImage.alt}
              sx={{
                width: '100%',
                maxWidth: 400,
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          </Box>
        )}
      </Dialog>
    </Box>
  );
}
